import { useEffect, useRef } from 'react'
import { usePearl } from '../lib/pearlchat'
import { describeView } from '../lib/views'
import { emphasisParts } from '../lib/turns'
import { useRoster } from '../lib/roster'
import type { View } from '../lib/views'

/**
 * Pearl on screen. Drawing only.
 *
 * The conversation, the request, the stream and the storage all live in
 * `lib/pearlchat.tsx`, and every rule about what is kept or restored lives in
 * `lib/pearlstore.ts` where it can be asserted. This file decides what it looks
 * like and nothing else.
 *
 * IT IS RENDERED IN TWO PLACES — the full Pearl tab, and the side panel that
 * stays open while she works through a screen. Both call `PearlView`, and both
 * read the same conversation from the provider, so opening the panel
 * mid-conversation continues it rather than starting a second one.
 */

const GREETING =
  "Hi Renee — I'm Pearl. Ask me about anyone on your roster: a phone number, an " +
  "email, when someone caps, who's Elite this year. I can also show you how to " +
  "do things in the dashboard — just ask. Or tell me what you're trying to do " +
  'and we’ll work through it together.'

/** The full tab. */
export default function Pearl() {
  return (
    <div className="pearl-page">
      <PearlView />
    </div>
  )
}

export function PearlView({ dense = false }: { dense?: boolean }) {
  const chat = usePearl()
  const roster = useRoster()
  const scroller = useRef<HTMLDivElement>(null)
  const box = useRef<HTMLTextAreaElement>(null)

  // Follow the reply as it is written.
  useEffect(() => {
    const el = scroller.current
    if (el) el.scrollTop = el.scrollHeight
  }, [chat.turns, chat.status, chat.busy])

  /**
   * Focus returns to the box when a reply finishes, in whichever of the two
   * places is currently on screen — the panel and the tab are never both
   * mounted, so there is no argument about which one gets it.
   */
  const wasBusy = useRef(chat.busy)
  useEffect(() => {
    if (wasBusy.current && !chat.busy) box.current?.focus()
    wasBusy.current = chat.busy
  }, [chat.busy])

  function onKeyDown(e: React.KeyboardEvent<HTMLTextAreaElement>) {
    // Enter sends; Shift+Enter makes a new line. Matches every chat app she uses.
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      void chat.ask(chat.draft)
    }
  }

  const empty = chat.turns.length === 0

  return (
    <div className={`pearl${dense ? ' dense' : ''}`}>
      {/*
        `pearl-scroll-empty` on an empty conversation only. Without it this
        div is `flex: 1` and always fills the panel, which pins the compose
        box to the very bottom no matter how little sits above it. Shrinking
        it to content height on the empty state lets the compose box sit
        directly under the greeting instead — see the matching CSS rule.
      */}
      <div className={`pearl-scroll${empty ? ' pearl-scroll-empty' : ''}`} ref={scroller}>
        {empty && (
          <div className="pearl-intro">
            <p>{GREETING}</p>
          </div>
        )}

        {chat.turns.map((t, i) => (
          <div key={i}>
            <div className={`bubble ${t.role}`}>
              {/*
                "Thinking…" belongs to the LAST turn only, and never to one
                carrying an offer.

                `chat.busy` is one flag for the whole conversation, and it was
                being tested against every turn. A reply that is nothing but a
                "Show me" card is legitimately stored with no words — it now
                survives a refresh — so the next question she asked turned that
                old card's bubble into a second "Thinking…", on an exchange from
                before the reload that was never going to fill in.
              */}
              {t.content === '' && chat.busy && i === chat.turns.length - 1 && !t.view ? (
                <span className="thinking">Thinking…</span>
              ) : (
                emphasisParts(t.content).map((p, j) =>
                  p.bold ? <strong key={j}>{p.text}</strong> : <span key={j}>{p.text}</span>,
                )
              )}
            </div>
            {t.view && (
              <div className="pearl-view">
                <div>
                  <strong>{t.view.label}</strong>
                  {/*
                    The count, before she presses anything. Pearl can ask for a
                    value nobody has, and a "0 agents" on the card is the
                    difference between noticing that here and discovering it on
                    an empty screen a click later.
                  */}
                  <div className="hint">
                    {roster.countFor(t.view)} agents
                    {describeView(t.view) ? ` · ${describeView(t.view)}` : ''}
                  </div>
                </div>
                <button className="btn sm" onClick={() => chat.accept(t.view as View)}>
                  Show me
                </button>
              </div>
            )}
          </div>
        ))}

        {chat.status && <div className="pearl-status">{chat.status}</div>}
        {chat.error && <div className="pearl-error">{chat.error}</div>}
      </div>

      <div className="pearl-compose">
        <textarea
          ref={box}
          value={chat.draft}
          rows={4}
          placeholder="Ask Pearl anything…"
          onChange={(e) => chat.setDraft(e.target.value)}
          onKeyDown={onKeyDown}
          disabled={chat.busy}
        />
        <button
          className="btn"
          onClick={() => void chat.ask(chat.draft)}
          disabled={chat.busy || chat.draft.trim() === ''}
        >
          {chat.busy ? 'Sending…' : 'Send'}
        </button>
        {/*
          Only once there is something to clear, and never while a reply is
          arriving. A conversation now survives navigating away and a refresh,
          so there has to be a way to put it down — but an always-present
          "Start again" beside Send is an invitation to lose your place.
        */}
        {!empty && !chat.busy && (
          <button className="btn ghost sm" onClick={chat.clear}>
            Start again
          </button>
        )}
      </div>
    </div>
  )
}
